<script setup lang="ts">
type Props = {
  // 'default' = página legada /user/wallet, layout original (ícone de regras
  // dentro da coluna estreita .bonusInfoxBox). 'v2' = Minha Conta V2
  // (ScreenWallet.vue) — ícone ancorado ao card inteiro, precisa do wrapper
  // relative/flex-wrap (mesmo padrão de prop variant do CoinsCard.vue).
  variant?: 'default' | 'v2';
};

const { variant = 'default' } = defineProps<Props>();

const { toggleModal } = useLayoutStore();

// fecha o modal do Minha Conta V2 ao navegar pra fora dele (mesmo padrão do
// CoinsCard.vue) — sem isso o middleware userV2Modal só reage a rotas
// /user/*, então ir pra /sports ou /games deixava o modal aberto por cima da
// página nova.
const { close: closeUserV2Modal } = useUserV2Modal();

const { walletIsLoading, walletBonusFormatted, showMoneyFormat } = useWallet();

const { getRolloverData } = useWalletStore();

const { bonusWallet, rollover, rolloverAccomplished } =
  storeToRefs(useWalletStore());

// Mesma conta do rollover em R$ que a faixa do header do jogo usa (ENG-817) —
// fonte única em usePlayWithBonus, sem recalcular `credit_hold * multiplier`
// aqui também.
const { rolloverAmount } = usePlayWithBonus();

const amountBet = computed(() => {
  return `${showMoneyFormat({ value: rolloverAmount.value.betAmount })} /
  ${showMoneyFormat({ value: rolloverAmount.value.targetAmount })}`;
});

const totalTime = computed(() => {
  const now = new Date();
  const expireDatetime = new Date(bonusWallet.value.expiry_datetime);

  return expireDatetime.getTime() > now.getTime()
    ? (expireDatetime.getTime() - now.getTime()) / 1000
    : 0;
});

const necessaryRollover = computed(() => {
  return rollover.value.rollover_count || 0;
});

const currentRolloverCount = computed(() => {
  return rollover.value.count || 0;
});

const rolloverRoundSuccess = computed(() => {
  if (!currentRolloverCount.value) return false;

  return currentRolloverCount.value >= necessaryRollover.value;
});

const walletHasBonus = computed<boolean>(() => {
  return !!bonusWallet.value.credit && totalTime.value > 0;
});

onMounted(() => {
  getRolloverData();
});
</script>

<template>
  <UserBox v-if="walletHasBonus">
    <div
      :class="[
        'flex justify-start items-stretch',
        { 'relative flex-wrap': variant === 'v2' },
      ]"
    >
      <!-- v2: ícone posicionado relativo ao card INTEIRO (esta div), não a
           .bonusInfoxBox — que é só a primeira das duas colunas (a outra,
           .rolloverProgressBox, tem flex-1 e ocupa o resto da largura). No
           default (legado), o ícone fica dentro de .topContent (que já é
           relative) e mantém a posição/tamanho originais. -->
      <UserWalletBonusRulesButton
        v-if="variant === 'v2'"
        :class="$style.rulesIcon"
        :is-rounded="true"
        :show-icon="true"
        :show-text="false"
        @toggle-withdraw-rules="toggleModal('bonus-rules')"
      />

      <div :class="$style.bonusInfoxBox">
        <div :class="$style.topContent">
          <UserWalletBonusRulesButton
            v-if="variant === 'default'"
            class="absolute top-0 right-0"
            :is-rounded="true"
            :show-icon="true"
            :show-text="false"
            @toggle-withdraw-rules="toggleModal('bonus-rules')"
          />

          <span :class="$style.currencyBonus">
            <DepositCurrencyButton
              :is-bonus-icon="true"
              :user-currency="$t('account.wallet.bonus')"
            />
          </span>

          <strong
            :class="[$style.walletAmount, { 'opacity-30': walletIsLoading }]"
          >
            {{ walletBonusFormatted }}
          </strong>

          <div
            v-if="totalTime"
            :class="$style.bonusCountdown"
          >
            <WidgetsCountdown :total-time="totalTime">
              <template #process="{ time }">
                <span :class="$style.countdownTitle">
                  {{ $t('account.wallet.bonus_expire_time') }}
                </span>
                <span :class="$style.countdownTime">
                  {{ `${time.hours}h : ${time.minutes}m : ${time.seconds}s` }}
                </span>
              </template>
              <template #finish>
                <span :class="$style.countdownTitle">
                  {{ $t('account.wallet.bonus_expired') }}
                </span>
              </template>
            </WidgetsCountdown>
          </div>

          <div :class="$style.bonusRolloverType">
            <p v-if="rollover.first_transaction === 'casino'">
              <NuxtIcon name="info" />
              <span>{{ $t('account.wallet.withdraw_bonus_available') }}</span>
            </p>
            <p v-else-if="rollover.first_transaction === 'sports'">
              <NuxtIcon name="info" />
              <span>
                {{ $t('account.wallet.withdraw_bonus_available_sports') }}
              </span>
            </p>
            <p v-else>
              <NuxtIcon name="info" />
              <span>
                {{ $t('account.wallet.withdraw_rollover_none') }}
              </span>
            </p>
          </div>
        </div>

        <!-- v2 não usa esta linha: os botões saíram pra fora da
             .bonusInfoxBox (que só tem min-width, não flex-grow — sem
             espaço pro tamanho do Depositar/Sacar), ver bloco full-width
             depois de .rolloverProgressBox. -->
        <div
          v-if="variant === 'default'"
          :class="$style.walletBoxBtns"
        >
          <NuxtLink
            v-if="rollover.first_transaction === 'sports'"
            :class="[$style.walletBtn, $style.accessBtn]"
            :to="localeLink('/sports')"
            @click="closeUserV2Modal"
          >
            {{ $t('account.wallet.access_sports') }}
          </NuxtLink>
          <NuxtLink
            v-else
            :class="[$style.walletBtn, $style.accessBtn]"
            :to="localeLink('/games/category/jogar-com-bonus')"
            @click="closeUserV2Modal"
          >
            {{ $t('account.wallet.access_games') }}
          </NuxtLink>

          <button
            v-if="rolloverAccomplished"
            :class="[$style.walletBtn, $style.withdrawBtn]"
            @click="toggleModal('bonus-withdraw')"
          >
            <span
              :class="$style.text"
              v-html="$t('account.wallet.withdraw_bonuses')"
            />
          </button>
          <button
            v-else
            :class="[$style.walletBtn, $style.withdrawBtn]"
            disabled
          >
            <span
              :class="$style.text"
              v-html="$t('account.wallet.withdraw_bonuses')"
            />
          </button>
        </div>
      </div>

      <!-- v2: linha própria, full-width (w-full força quebra de linha no
           flex-wrap do wrapper) — mesmo tamanho do Depositar/Sacar, sem
           depender da largura estreita da .bonusInfoxBox. Fica ACIMA do
           Rollover, na mesma posição que ocupava antes. flex-wrap porque
           agora são 3 botões (acesso, sacar, histórico) — em telas
           estreitas o histórico quebra pra linha de baixo. -->
      <div
        v-if="variant === 'v2'"
        class="flex w-full flex-wrap gap-[8px]"
      >
        <NuxtLink
          v-if="rollover.first_transaction === 'sports'"
          class="flex h-[40px] flex-1 items-center justify-center rounded-[12px] bg-v2-primary px-[16px] text-[14px] font-semibold leading-[20px] text-white"
          :to="localeLink('/sports')"
          @click="closeUserV2Modal"
        >
          <!-- abaixo de 380px "Acessar Esportes" não cabe numa linha só
               dentro do h-[40px] fixo — versão curta só nesse recorte. -->
          <span class="hidden max-[380px]:inline">
            {{ $t('account.wallet.access_sports_short') }}
          </span>
          <span class="max-[380px]:hidden">
            {{ $t('account.wallet.access_sports') }}
          </span>
        </NuxtLink>
        <NuxtLink
          v-else
          class="flex h-[40px] flex-1 items-center justify-center rounded-[12px] bg-v2-primary px-[16px] text-[14px] font-semibold leading-[20px] text-white"
          :to="localeLink('/games/category/jogar-com-bonus')"
          @click="closeUserV2Modal"
        >
          <!-- idem: "Acessar Jogos" quebra linha abaixo de 380px -->
          <span class="hidden max-[380px]:inline">
            {{ $t('account.wallet.access_games_short') }}
          </span>
          <span class="max-[380px]:hidden">
            {{ $t('account.wallet.access_games') }}
          </span>
        </NuxtLink>

        <!-- sempre "Sacar Bônus", sem versão curta abaixo de 380px (ao
             contrário dos botões acima). No mobile ele vai pro final
             (order-last) e ocupa a linha inteira sozinho (flex-none + w-full
             — flex-1 usaria flex-basis:0 e ignoraria o w-full), deixando Ver
             Jogos/Ver Esportes + Histórico juntos na primeira linha. No md+
             volta a ficar no meio, lado a lado. -->
        <button
          class="border-solid order-last flex h-[40px] w-full flex-none items-center justify-center rounded-[12px] border-2 border-white px-[16px] text-[14px] font-semibold leading-[20px] text-white disabled:cursor-not-allowed disabled:opacity-40 md:order-none md:w-auto md:flex-1"
          :disabled="!rolloverAccomplished"
          @click="toggleModal('bonus-withdraw')"
        >
          {{ $t('account.wallet.withdraw_bonuses') }}
        </button>

        <NuxtLink
          class="border-solid flex h-[40px] flex-1 items-center justify-center rounded-[12px] border-2 border-white px-[16px] text-[14px] font-semibold leading-[20px] text-white"
          :to="localeLink('/user/wallet/transactions?filter=bonus')"
        >
          <span class="hidden max-[380px]:inline">
            {{ $t('account.wallet.view_history_short') }}
          </span>
          <span class="max-[380px]:hidden">
            {{ $t('account.wallet.view_history') }}
          </span>
        </NuxtLink>
      </div>

      <div :class="$style.rolloverProgressBox">
        <span
          :class="[
            $style.rolloverStatus,
            !rollover.success ? 'opacity-40' : 'opacity-100',
          ]"
        >
          {{ $t('account.wallet.rollover') }}
          <span v-if="rollover.first_transaction === 'casino'">
            {{ $t('account.wallet.casino').toLocaleLowerCase() }}
          </span>
          <span v-if="rollover.first_transaction === 'sports'">
            {{ $t('account.wallet.sports').toLocaleLowerCase() }}
          </span>
        </span>

        <div
          :class="[
            $style.bonusRolloverContainer,
            { [$style.isInactive]: !rollover.success },
          ]"
        >
          <div :class="$style.progressItem">
            <div :class="$style.rollLabel">
              <span :class="$style.rollValue">
                {{ $t('account.wallet.amount_bet') }}
              </span>
              <span
                :class="[
                  $style.rollValue,
                  $style.isValue,
                  rolloverRoundSuccess ? 'text-primary' : 'text-texts',
                ]"
              >
                {{ amountBet }}
              </span>
              <span :class="$style.rollValue">
                {{ $t('account.wallet.rollover_target') }}
              </span>
            </div>
            <UserWalletProgressBar
              :class="$style.rollProgress"
              :height="10"
              :max="rolloverAmount.targetAmount"
              :progress="rolloverAmount.betAmount"
            />
          </div>

          <div :class="$style.progressItem">
            <div :class="$style.rollLabel">
              <span :class="$style.rollValue">
                {{ $t('account.wallet.rounds_played') }}
              </span>
              <span
                :class="[
                  $style.rollValue,
                  $style.isValue,
                  rolloverRoundSuccess ? 'text-primary' : 'text-texts',
                ]"
              >
                {{ currentRolloverCount }} / {{ necessaryRollover }}
              </span>
              <span :class="$style.rollValue">
                {{ $t('account.wallet.play_goal') }}
              </span>
            </div>
            <UserWalletProgressBar
              :class="$style.rollProgress"
              :height="10"
              :max="necessaryRollover"
              :progress="currentRolloverCount || 0"
            />
          </div>
        </div>
      </div>
    </div>
  </UserBox>

  <!-- v2: sem bônus ativo, o card antigo simplesmente sumia (v-if acima) —
       o Minha Conta V2 mostra um estado vazio com link pro histórico
       (aba "Bônus" que acabamos de adicionar em ScreenTransactionsHistory,
       via ?filter=bonus). Só v2: o legado continua só sumindo. -->
  <div
    v-else-if="variant === 'v2'"
    class="border-solid flex w-full flex-col gap-[12px] rounded-[16px] border border-v2-default/40 bg-v2-default/20 p-[16px]"
  >
    <p class="text-[14px] font-semibold text-white">
      {{ $t('account.wallet.bonus_balance_title') }}
    </p>
    <p class="text-[12px] text-v2-default-800">
      {{ $t('account.wallet.bonus_balance_empty') }}
    </p>
    <NuxtLink
      class="border-solid flex h-[40px] w-full items-center justify-center rounded-[12px] border-2 border-white px-[16px] text-[14px] font-semibold leading-[20px] text-white"
      :to="localeLink('/user/wallet/transactions?filter=bonus')"
    >
      {{ $t('account.wallet.view_history') }}
    </NuxtLink>
  </div>
</template>

<style module lang="postcss">
/* mesmo canto do .walletIcon em RealWallet.vue — irmã de .bonusInfoxBox e
   .rolloverProgressBox (ver template), não descendente delas, senão o
   seletor vira algo tipo ".bonusInfoxBox .rulesIcon" e não bate com o
   elemento (que não é mais filho dessas divs). */
.rulesIcon {
  /* mesmo tamanho fixo do .walletIcon (32px, 40px a partir do lg:) — o botão
     antigo não tem tamanho fixo (cresce com o padding do .btn-accent), então
     só travar w-8/h-8 aqui não bastaria; overflow-hidden + flex center
     encaixa o conteúdo dele nessa caixa sem precisar sobrescrever o padding
     interno do componente.

     z-10: .bonusInfoxBox é `relative` (pro variant default) e vem depois
     deste elemento no DOM — dois "position" com z-index:auto empilham por
     ordem no DOM, então sem isso o bonusInfoxBox pinta por cima e rouba o
     clique nessa área. Abaixo de ~380px o min-w do bonusInfoxBox quase
     preenche o card e passa a sobrepor de fato o ícone, e o clique parava
     de funcionar mesmo o ícone continuando visível. */
  @apply absolute top-0 right-0 z-10 flex size-8 items-center justify-center overflow-hidden lg:size-10;
}

.bonusInfoxBox {
  @apply min-w-[280px] md:min-w-[280px] 2xl:min-w-[360px] relative min-h-full flex flex-col;

  .topContent {
    @apply pb-2 relative flex flex-col items-start justify-start;

    .currencyBonus {
      @apply text-texts/70 inline-flex pb-1;
    }

    .walletAmount {
      @apply inline-block text-xl text-texts leading-none font-medium lg:text-3xl pr-16 md:pr-24 pb-1;
    }

    .bonusCountdown {
      @apply text-2xs mt-1 mb-4;
      .countdownTitle {
        @apply bg-error rounded-l py-1 px-1.5 font-semibold;
      }
      .countdownTime {
        @apply bg-white text-black rounded-r py-1 px-2 font-bold;
      }
    }

    .bonusRolloverType {
      @apply flex flex-col gap-3;
      p {
        @apply flex gap-2 items-center text-texts/70 text-2xs;
      }
    }
  }

  .walletBoxBtns {
    @apply flex items-center justify-between gap-2 w-full mt-auto;

    .walletBtn {
      @apply py-2 px-4 rounded-md flex-1 min-w-28 text-xs md:text-sm flex items-center justify-center gap-2 whitespace-nowrap;

      &.accessBtn {
        @apply bg-primary/15 text-primary/70 font-semibold;
      }

      &.withdrawBtn {
        @apply bg-white text-bg-primary font-semibold;

        &[disabled]:not([disabled='false']) {
          @apply opacity-40 cursor-not-allowed;
        }

        &.isCountdown {
          @apply flex flex-col items-center gap-1 px-3 py-0 cursor-not-allowed flex-1 min-h-full;
        }

        .icon {
          @apply text-sm;
        }
      }
    }
  }
}

.rolloverProgressBox {
  /* sem barra/margem lateral em nenhum breakpoint (só fazia sentido lado a
     lado, e agora o card do bônus é largura cheia — vira sempre um bloco
     normal abaixo, com respiro maior no topo pra não colar nos botões).
     min-w só a partir do sm: abaixo disso (telas de celular estreitas) ele
     forçava a caixa a estourar o card — sem min-w o flex-1 já ocupa a
     largura toda disponível. */
  @apply mt-6 flex flex-1 flex-col items-start justify-center sm:min-w-[320px] md:min-w-[250px] 2xl:min-w-[360px];

  .rolloverStatus {
    @apply py-[3px] px-1.5 leading-3 inline-block rounded text-2xs font-semibold bg-warning text-bg-primary;
  }

  .bonusRolloverContainer {
    @apply mt-2 w-full;

    &.isInactive {
      @apply opacity-50;
    }

    .progressItem {
      /* nowrap só a partir do sm: abaixo disso label+valor+label não cabem
         numa linha só — melhor quebrar entre eles do que estourar a div. */
      @apply flex flex-col py-1.5 sm:whitespace-nowrap;

      .rollLabel {
        @apply flex flex-wrap items-center justify-between gap-2 w-full sm:flex-nowrap;

        .rollValue {
          @apply text-2xs text-center;

          &.isValue {
            @apply font-semibold;
          }
        }
      }

      .rollProgress {
        @apply col-span-3;
      }
    }
  }
}
</style>
